import './touch.css';

import { uiText } from './text.js';
import { isTouch } from './touch.js';

/**
 * What every screen needs on a phone, installed once from main.ts (ADR 0043):
 *
 * - a long press on anything with a `title` shows it: on a desktop that
 *   tooltip comes with the hover, and a finger never hovers. The click that
 *   ends the press is swallowed, so reading a spell's description does not
 *   cast it.
 * - `--keyboard-inset` on the root: how much of the bottom of the screen the
 *   on-screen keyboard covers, so the chat can sit above it. iOS shrinks only
 *   the visual viewport; Android is told to do the same by the viewport meta
 *   (`interactive-widget=resizes-visual`), so the game never resizes (and
 *   never changes zoom) because a keyboard opened.
 */

/** Held this long without moving, a press is a question, not a tap. */
const LONG_PRESS_MS = 450;
/** A finger drifting further than this is scrolling, not pressing. */
const PRESS_SLOP_PX = 10;
const HINT_SHOWN_MS = 2600;

export function installTouchBasics(): void {
  installLongPressTitles();
  installKeyboardInset();
}

function installLongPressTitles(): void {
  const hint = document.createElement('div');
  hint.className = 'touch-hint';
  hint.setAttribute('role', 'status');
  hint.hidden = true;
  document.body.append(hint);

  let timer: number | undefined;
  let hideTimer: number | undefined;
  let start: { x: number; y: number } | undefined;
  let swallowClick = false;

  const cancel = (): void => {
    window.clearTimeout(timer);
    timer = undefined;
    start = undefined;
  };

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (event.pointerType !== 'touch') return;
      const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('[title]');
      const text = target?.title;
      if (target === null || target === undefined || text === undefined || text === '') return;
      start = { x: event.clientX, y: event.clientY };
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = undefined;
        swallowClick = true;
        show(hint, target, text);
        window.clearTimeout(hideTimer);
        hideTimer = window.setTimeout(() => {
          hint.hidden = true;
        }, HINT_SHOWN_MS);
      }, LONG_PRESS_MS);
    },
    true,
  );
  document.addEventListener(
    'pointermove',
    (event) => {
      if (start === undefined) return;
      if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > PRESS_SLOP_PX) cancel();
    },
    true,
  );
  document.addEventListener('pointerup', cancel, true);
  document.addEventListener('pointercancel', cancel, true);
  document.addEventListener(
    'click',
    (event) => {
      if (!swallowClick) return;
      swallowClick = false;
      event.preventDefault();
      event.stopPropagation();
    },
    true,
  );
  // The long press is ours: no text selection bubble, no « save image » sheet.
  document.addEventListener('contextmenu', (event) => {
    if (!isTouch()) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, textarea') !== null) return;
    event.preventDefault();
  });
}

/** Above the pressed element, kept inside the screen. */
function show(hint: HTMLElement, target: HTMLElement, text: string): void {
  hint.textContent = uiText(text);
  hint.hidden = false;
  const box = target.getBoundingClientRect();
  const width = hint.offsetWidth;
  const height = hint.offsetHeight;
  const left = Math.min(
    Math.max(8, box.left + box.width / 2 - width / 2),
    window.innerWidth - width - 8,
  );
  const above = box.top - height - 8;
  const top = above >= 8 ? above : Math.min(box.bottom + 8, window.innerHeight - height - 8);
  hint.style.left = `${String(Math.round(left))}px`;
  hint.style.top = `${String(Math.round(top))}px`;
}

function installKeyboardInset(): void {
  const viewport = window.visualViewport;
  if (viewport === null) return;
  const update = (): void => {
    const covered = window.innerHeight - (viewport.height + viewport.offsetTop);
    document.documentElement.style.setProperty(
      '--keyboard-inset',
      `${String(Math.max(0, Math.round(covered)))}px`,
    );
  };
  viewport.addEventListener('resize', update);
  viewport.addEventListener('scroll', update);
  update();
}
